import * as React from "react";
import { useCallback, useState } from "react";
import moment from "moment";
import { qualifyUrl } from "util/URLUtils";
import {
  createExportFilename,
  escapeFilename,
  mimeTypeMapper,
  BinaryFilesExtentions
} from "search/ExportWidgetAction/util/WidgetExportUtils";
import { parseSearchParams } from "security-app/hooks/api/securityEventsAPI";
import { fetchBinaryFile, fetchTextFile } from "util/FileDownloadUtils";
import useUserDateTime from "hooks/useUserDateTime";
import AggregationWidgetExportDropdown from "search/ExportWidgetAction/AggregationWidgetExportDropdown";
import { supportedAggregationExportFormats } from "search/Constants";
import { MenuItem } from "components/bootstrap";
import assertUnreachable from "logic/assertUnreachable";
import { isTypeRelativeWithStartOnly } from "views/typeGuards/timeRange";
import { adjustFormat } from "util/DateTime";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
const getUrl = (type, filename) => qualifyUrl(`/plugins/org.graylog.plugins.securityapp/security_events/histogram/${type}/${filename}`);
const getPdfUrl = () => qualifyUrl("plugins/org.graylog.plugins.widgetexport/widgetexport/export");
const widgetType = (type) => {
  switch (type) {
    case "alerts":
      return "events_metrics_widget";
    case "security_events":
    case "tactics":
    case "techniques":
      return "security_events_metrics_widget";
    default:
      return assertUnreachable(type, "Invalid widget export type");
  }
};
const renderType = (type) => {
  switch (type) {
    case "alerts":
      return "EVENTS_HISTOGRAM";
    case "security_events":
      return "SECURITY_EVENTS_HISTOGRAM";
    case "tactics":
    case "techniques":
      return "MITRE_CATEGORY_HISTOGRAM";
    default:
      return assertUnreachable(type, "Invalid widget export type");
  }
};
const prepareTimeRangeForGraph = (timerange) => {
  if (isTypeRelativeWithStartOnly(timerange)) {
    const from = moment().utc().subtract(timerange.range, "seconds");
    const to = moment();
    return [adjustFormat(from, "internal"), adjustFormat(to, "internal")];
  }
  return [timerange.from, timerange.to];
};
const exportResults = (type, widgetTitle, searchParams, extension, timezone) => {
  const { timerange } = parseSearchParams(searchParams, timezone);
  const [from, to] = prepareTimeRangeForGraph(timerange);
  const fileName = escapeFilename(createExportFilename(widgetTitle, from, to, extension));
  const mimeType = mimeTypeMapper[extension];
  const isPdf = extension === "pdf";
  const request = isPdf ? {
    data: { elements: [], type: "object_list_data" },
    widget: {
      orientation: "landscape",
      widget_type: widgetType(type),
      render_type: renderType(type),
      search_parameters: searchParams,
      position: { col: 1, row: 1, height: 2, width: 12 }
    }
  } : parseSearchParams(searchParams, timezone);
  const url = isPdf ? getPdfUrl() : getUrl(type, fileName);
  if (BinaryFilesExtentions.includes(extension)) return fetchBinaryFile("POST", url, request, mimeType, fileName);
  return fetchTextFile("POST", url, request, mimeType, fileName);
};
const typeToTelemetryEvent = {
  alerts: TELEMETRY_EVENT_TYPE.ALERTS_AND_EVENTS.EXPORT_HISTOGRAM,
  tactics: TELEMETRY_EVENT_TYPE.SECURITY_APP.SECURITY_EVENTS.EXPORT_CATEGORIES_HISTOGRAM,
  techniques: TELEMETRY_EVENT_TYPE.SECURITY_APP.SECURITY_EVENTS.EXPORT_CATEGORIES_HISTOGRAM
};
const typeToAppSection = {
  alerts: "alerts-and-events",
  tactics: "security-events",
  techniques: "security-events"
};
const ExportResultsButton = ({ type, title, searchParams, disabled = false }) => {
  const { userTimezone } = useUserDateTime();
  const sendTelemetry = useSendTelemetry(typeToAppSection[type]);
  const [isExporting, setIsExporting] = useState(false);
  const onExportAggregationWidget = useCallback(
    (extension) => {
      sendTelemetry(typeToTelemetryEvent[type], {
        app_action_value: { extension }
      });
      setIsExporting(true);
      return exportResults(type, title, searchParams, extension, userTimezone).finally(() => {
        setIsExporting(false);
      });
    },
    [type, sendTelemetry, title, searchParams, userTimezone]
  );
  return /* @__PURE__ */ React.createElement(AggregationWidgetExportDropdown, { isExporting }, supportedAggregationExportFormats.map(({ id: extension, title: extensionTitle }) => /* @__PURE__ */ React.createElement(MenuItem, { disabled, key: extension, onSelect: () => onExportAggregationWidget(extension) }, extensionTitle)));
};
export default ExportResultsButton;
